import type { ReactNode } from 'react'
import type { CustomerAreaHeaderAction, CustomerAreaHeaderStat } from './customerAreaLayoutTypes'

const joinClassNames = (...classNames: Array<string | null | undefined | false>) =>
  classNames.filter(Boolean).join(' ')

const getActionClassName = (variant: CustomerAreaHeaderAction['variant']) => {
  if (variant === 'danger') {
    return 'ca-btn-secondary border-(--color-status-error-border) text-(--color-status-error) hover:border-(--color-status-error-border)'
  }
  if (variant === 'primary') {
    return 'ca-btn-primary'
  }
  return 'ca-btn-secondary'
}

type CustomerAreaHeaderSectionProps = {
  eyebrow: string
  title: string
  subtitle?: string
  meta?: ReactNode
  headerContent?: ReactNode
  titleClassName?: string
  subtitleClassName?: string
  headerClassName?: string
  actions?: CustomerAreaHeaderAction[]
  stats?: CustomerAreaHeaderStat[]
}

export const CustomerAreaHeaderSection = ({
  eyebrow,
  title,
  subtitle,
  meta,
  headerContent,
  titleClassName,
  subtitleClassName,
  headerClassName,
  actions,
  stats,
}: CustomerAreaHeaderSectionProps) => (
  <section className="ca-card-shell ca-theme-transition overflow-hidden">
    <div className={joinClassNames('space-y-4 px-5 py-4 sm:px-6 sm:py-5', headerClassName)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-(--color-text-muted)">{eyebrow}</p>
          <h1
            className={joinClassNames(
              'mt-1.5 text-2xl font-semibold tracking-tight text-(--color-text-primary) sm:text-3xl',
              titleClassName,
            )}
          >
            {title}
          </h1>
          {subtitle ? (
            <p
              className={joinClassNames(
                'mt-2 text-sm leading-relaxed text-(--color-text-secondary)',
                subtitleClassName,
              )}
            >
              {subtitle}
            </p>
          ) : null}
          {meta ? <p className="mt-2 text-xs text-(--color-text-muted)">{meta}</p> : null}
        </div>
        {actions?.length ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={action.onClick}
                className={joinClassNames(
                  'ca-theme-transition inline-flex items-center rounded-[10px] px-3 py-1.5 text-sm font-semibold',
                  getActionClassName(action.variant),
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {headerContent ? <div>{headerContent}</div> : null}

      {stats?.length ? (
        <div className="ca-surface-muted ca-theme-transition">
          <div className="grid divide-y divide-(--color-border-default) sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {stats.map((stat, index) => {
              const key = stat.id ?? `${stat.label}-${index}`
              const content = (
                <>
                  <p className="text-[10px] font-medium uppercase tracking-wide text-(--color-text-muted)">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-3xl leading-none font-semibold tracking-tight text-(--color-text-primary)">
                    {stat.value}
                  </p>
                  {stat.meta ? <p className="mt-1 text-[11px] text-(--color-text-muted)">{stat.meta}</p> : null}
                </>
              )
              if (stat.onClick) {
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={stat.onClick}
                    className="ca-theme-transition min-w-0 px-3 py-2.5 text-left hover:bg-(--color-bg-card) sm:px-4"
                  >
                    {content}
                  </button>
                )
              }
              return (
                <div key={key} className="min-w-0 px-3 py-2.5 sm:px-4">
                  {content}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  </section>
)
